import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Question, ArrowRight, Sparkle, CaretLeft, CaretRight } from '@phosphor-icons/react';
import CustomPagination from '../../../components/CustomPagination';
import SignInProgressCta from '../../../components/SignInProgressCta';
import type { ViewMode } from '../hooks/useViewMode';
import type { SrpPaginationViewModel, SrpStaticStrings } from '../../../features/srp/SrpViewModel';

export interface SearchPaginationProps {
  viewMode: ViewMode;
  helpTrayOpen: boolean;
  setHelpTrayOpen: (open: boolean) => void;
  pageContextPathname?: string;
  className?: string;
  /** When provided with getPageUrl, render static pagination (same DOM as CustomPagination). */
  staticPagination?: SrpPaginationViewModel;
  getPageUrl?: (page: number) => string;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided with static branch, use instead of useIntl. */
  staticStrings?: SrpStaticStrings;
}

const baseItemClasses =
  'min-w-[44px] min-h-[44px] md:min-w-[48px] md:min-h-[48px] px-4 md:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all duration-200 ease-out active:scale-95 flex items-center justify-center touch-manipulation select-none';

/**
 * Search pagination component - contains pagination controls, keyboard shortcuts button, and feedback button.
 * In static mode (staticPagination + getPageUrl) renders same nav structure with anchor links only.
 */
export const SearchPagination: React.FC<SearchPaginationProps> = ({
  viewMode,
  helpTrayOpen,
  setHelpTrayOpen,
  pageContextPathname,
  className = '',
  staticPagination,
  getPageUrl,
  staticStrings,
}) => {
  if (viewMode === 'map') {
    return null;
  }

  const useStatic = staticPagination != null && getPageUrl != null && staticStrings != null;

  if (useStatic && staticPagination && getPageUrl && staticStrings) {
    const { currentPage, nbPages, prevUrl, nextUrl } = staticPagination;
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, currentPage - half);
    let end = start + (windowSize - 1);
    if (end > nbPages - 1) {
      end = nbPages - 1;
      start = Math.max(0, end - (windowSize - 1));
    }
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const isFirstPage = currentPage <= 0;
    const isLastPage = currentPage >= nbPages - 1;
    const ariaLabel = staticStrings?.paginationAriaLabel ?? 'Job search results pagination';
    const firstLabel = staticStrings?.paginationFirst ?? 'First';
    const prevLabel = staticStrings?.paginationPrevious ?? 'Previous';
    const nextLabel = staticStrings?.paginationNext ?? 'Next';
    const lastLabel = staticStrings?.paginationLast ?? 'Last';

    return (
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-4 pb-4 items-center justify-center ${className}`}
        aria-label={ariaLabel}
      >
        <div className="flex justify-center py-2 md:py-3">
          <div className="flex flex-col items-center gap-3 w-full max-w-4xl px-4">
            <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-3 w-full">
              <div className="flex justify-center items-center gap-2 flex-wrap" role="list">
              {!isFirstPage && (
                <>
                  <a
                    href={getPageUrl(0)}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    {firstLabel}
                  </a>
                  <a
                    href={prevUrl ?? getPageUrl(currentPage - 1)}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    <div className="flex items-center gap-1.5">
                      <CaretLeft className="w-4 h-4 text-[#3D3529] dark:text-[#C4A882]" />
                      {prevLabel}
                    </div>
                  </a>
                </>
              )}
              {pages.map((page) => {
                const label = page + 1;
                const isActive = currentPage === page;
                return (
                  <a
                    key={page}
                    href={getPageUrl(page)}
                    aria-label={`Page ${label}`}
                    className={`${baseItemClasses} ${isActive
                      ? 'bg-[#3B5E45] dark:bg-[#1A2E22] text-white dark:text-[#98CFA8] font-semibold shadow-md dark:shadow-none border border-[#2A4533] dark:border-[#4A3520]'
                      : 'bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none'
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
              {!isLastPage && (
                <>
                  <a
                    href={nextUrl ?? getPageUrl(currentPage + 1)}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    <div className="flex items-center gap-1.5">
                      {nextLabel}
                      <CaretRight className="w-4 h-4 text-[#3D3529] dark:text-[#C4A882]" />
                    </div>
                  </a>
                  <a
                    href={getPageUrl(nbPages - 1)}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    {lastLabel}
                  </a>
                </>
              )}
              </div>
              {/* Match interactive layout: keyboard hint placeholder in-row + sign-in CTA so page height doesn't jump on swap */}
              <div className="hidden md:flex items-center shrink-0 justify-center">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#7A6E5F] dark:text-[#B8A48C]"
                  aria-hidden
                >
                  <Question className="w-6 h-6" />
                </span>
              </div>
            </div>
            <div className="mt-4 px-4">
              <div className="relative overflow-hidden rounded-[28px] border border-white/40 bg-gradient-to-br from-[#3B5E45] via-[#5A8A68] to-[#3B5E45] text-[#F2EADF] shadow-[0_24px_48px_-20px_rgba(59,94,69,0.45)] dark:border-[#98CFA8]/25 dark:shadow-none dark:from-[#1A2E22] dark:via-[#2A4533] dark:to-[#1A2E22]">
                <div aria-hidden className="pointer-events-none absolute inset-0">
                  <div className="absolute -left-10 top-6 h-40 w-40 rounded-full bg-white/20 blur-3xl dark:bg-[#98CFA8]/15" />
                  <div className="absolute bottom-[-60px] right-[-20px] h-48 w-48 rounded-full bg-[#C8DDCE]/30 blur-3xl dark:bg-[#5A8A68]/15" />
                </div>
                <div className="relative flex flex-col gap-4 px-4 py-5 sm:gap-6 sm:px-6 sm:py-8 lg:px-10 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center justify-center lg:justify-start">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-xl shadow-inner shadow-white/10 sm:h-16 sm:w-16">
                      <Sparkle className="h-6 w-6 text-white sm:h-8 sm:w-8" />
                    </div>
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/80 sm:text-xs md:text-sm">
                      {staticStrings.signInCta.badge}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold sm:mt-3 sm:text-2xl md:text-3xl lg:text-[32px]">
                      {staticStrings.signInCta.title}
                    </h2>
                    <p className="mt-2 text-sm text-white/90 sm:mt-3 sm:text-base md:text-lg">
                      {staticStrings.signInCta.description}
                    </p>
                  </div>
                  <div className="flex w-full justify-center lg:w-auto lg:justify-end">
                    <a
                      href="/auth/login"
                      className="group relative inline-flex w-full items-center justify-center rounded-2xl bg-[#F8F5F0] px-4 py-2.5 text-sm font-semibold text-[#3B5E45] shadow-lg shadow-black/10 dark:shadow-none transition-all duration-200 hover:shadow-xl dark:bg-[#F2EADF] dark:text-[#2A4533] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3B5E45] sm:px-5 sm:py-3 sm:text-base md:w-auto"
                    >
                      <span className="flex items-center gap-2">
                        {staticStrings.signInCta.button}
                        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const intl = useIntl();
  return (
    <>
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-4 pb-4 items-center justify-center ${className}`}
        aria-label={intl.formatMessage({
          id: 'pagination.ariaLabel',
          defaultMessage: 'Job search results pagination',
          description: 'Accessibility label for pagination navigation controls',
        })}
      >
        <CustomPagination
          trailingSlot={
            <button
              type="button"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] hover:bg-[#C8DDCE] dark:hover:bg-[#342415] text-[#3D3529] dark:text-[#C4A882] transition-all duration-200 shadow dark:shadow-none"
              aria-label="Show keyboard shortcuts help"
              onClick={() => setHelpTrayOpen(true)}
              tabIndex={0}
            >
              <Question className="w-6 h-6" />
              <span className="sr-only">Keyboard Shortcuts</span>
            </button>
          }
        />
        <SignInProgressCta className="mt-4 px-4" />
      </nav>
    </>
  );
};
